import { atom } from 'jotai';

import type { NotePushMapping } from '../../../contracts/projects/note-push-mapping.types';

/**
 * Bridge that lets the note-push verification panel (rendered deep in the
 * artifact canvas) route the CONFIRMED mapping into an actual turn — the
 * structured run-local handoff that replaces the ephemeral overrides which never
 * reached execution. Set by the mounted project chat (which owns the
 * transport + flow identity); `null` when no chat is mounted or the run's flow
 * identity (workspace + session) can't be resolved, so the panel can gate its
 * confirm affordance on `ready`.
 */
export interface NotePushApplyBridge {
  /**
   * Compile the plan from the confirmed mapping and submit it as `note_push_plan`
   * on a turn. A no-op when the mapping is incomplete (the panel gates on
   * `isPlanComplete` so this isn't reached) or identity is missing.
   */
  applyMapping: (mapping: NotePushMapping) => void;
  /**
   * Route an INCOMPLETE mapping to discovery: submit a plain turn asking the
   * agent to discover the on-screen fields for the unmapped sections. No plan —
   * the backend rejects incomplete plans, so we never send one.
   */
  discover: () => void;
  /** Flow identity (workspace + session) is resolvable, so a plan can be built. */
  ready: boolean;
  /**
   * A turn submit is in flight on the owning chat's transport. The panel disables
   * Confirm & apply / Discover while true so a double-click can't POST the same
   * `note_push_plan` twice (duplicate approvals / agent writes). Optional so
   * existing bridge constructors and test builders stay valid.
   */
  submitting?: boolean;
}

export const projectChatNotePushApplyAtom = atom<NotePushApplyBridge | null>(null);

/**
 * Outcome of the most recent apply submit, for the panel to render:
 *   - `idle` — no outcome to show (default; also reset on every new apply).
 *   - `stale-note-revision` — the turn 409'd because the note changed under
 *     the confirmed mapping. The bridge already re-fetched the note; the panel
 *     tells the user to review + confirm again. The stale plan is never
 *     retried.
 *   - `already-placed` — the turn 409'd because every planned field is already
 *     verified-placed. Terminal, friendly: the panel offers Done instead of a
 *     re-write.
 *   - `identity-mismatch` — the turn 409'd because the plan's flow identity no
 *     longer matches the run (workspace/session). Surfaced distinctly; the
 *     mapping must be re-confirmed against the current run.
 *   - `route-to-discovery` — the turn 422'd because the plan is incomplete. The
 *     bridge already routed to discovery; the panel clears its preview and shows
 *     the discovery-in-progress state (no error toast).
 */
export type NotePushApplyStatus =
  | 'idle'
  | 'stale-note-revision'
  | 'already-placed'
  | 'identity-mismatch'
  | 'route-to-discovery';

export const notePushApplyStatusAtom = atom<NotePushApplyStatus>('idle');

/**
 * Liveness epoch for the note-push apply flow, bumped each time the verification
 * panel unmounts (closes / switches). An in-flight apply captures the epoch at
 * submit time and drops its late outcome if the epoch has since advanced — so a
 * conflict (`stale-note-revision` / `already-placed`) resolving AFTER the panel
 * closed can't write a stale status into {@link notePushApplyStatusAtom} that a
 * later panel would then open into. (The apply-side counterpart to warm-apply's
 * `stillCurrent()` await re-checks.)
 */
export const notePushApplyEpochAtom = atom(0);
